<template>
  <UICollapse
    id="ratingId"
    :arrow-placement="'left'"
    :default-expanded-names="[t('common.generalSettings')]"
  >
    <UICollapseItem
      id="rating-general-settings"
      :title="t('common.generalSettings')"
      :name="t('common.generalSettings')"
    >
      <div class="form-group">
        <div class="flex space-x-3">
          <UITextXsMedium>{{ t("common.label") }}</UITextXsMedium>
        </div>
        <UIInput
          id="label"
          class="standard-input-ghl"
          :model-value="ratingElement.label"
          @update:model-value="updateLabel"
          @blur="store.addToHistory()"
        ></UIInput>
      </div>
      <div class="form-group">
        <LabelAlignment
          :label-alignment="ratingElement.labelAlignment"
          :is-element-level="true"
          @update:label-alignment="handleLabelAlignment"
        ></LabelAlignment>
      </div>
      <div class="form-group">
        <div class="flex space-x-3">
          <UITextXsMedium>{{ t("common.shortLabel") }}</UITextXsMedium>
        </div>
        <UIInput
          id="shortLabel"
          class="standard-input-ghl"
          :model-value="ratingElement.shortLabel"
          @update:model-value="updateShortLabel"
          @blur="store.addToHistory()"
        >
        </UIInput>
      </div>
      <div v-if="product === PRODUCT_TYPE.FORM" class="form-group">
        <div class="flex justify-between pb-2">
          <UITextXsMedium>{{ t("common.style.fieldWidth") }}</UITextXsMedium>
        </div>
        <UIInputGroup>
          <UIInputNumber
            id="fieldWidthKey"
            size="large"
            class="w-100"
            :min="1"
            :max="100"
            :show-button="false"
            :model-value="ratingElement.fieldWidthPercentage"
            @update:model-value="updateFieldWidthPercentage"
            @blur="getFieldWidthPercentage"
          ></UIInputNumber
          ><UIInputGroupLabel>%</UIInputGroupLabel>
        </UIInputGroup>
      </div>
      <div class="form-check-req-hid mb-2">
        <UICheckbox
          id="fieldRequired"
          :disabled="isRequiredFieldDisable"
          :checked="ratingElement.required"
          @update:checked="updateRequired"
          >{{ t("common.required") }}</UICheckbox
        >
      </div>
    </UICollapseItem>
    <UICollapseItem
      id="rating-settings"
      :title="t('common.rating.ratingSettings')"
      :name="t('common.rating.ratingSettings')"
    >
      <div class="form-group">
        <div class="flex space-x-3">
          <UITextXsMedium>{{ t("common.rating.icon") }}</UITextXsMedium>
        </div>
        <div class="flex items-start justify-start gap-2 self-stretch">
          <div
            v-for="icon in RATING_ELEMENTS"
            :key="icon.name"
            class="flex h-12 w-12 cursor-pointer items-center justify-center transition-colors duration-200"
            :class="{
              'rating-icon-selected': ratingElement.iconType === icon.name,
              'rating-icon-unselected': ratingElement.iconType !== icon.name,
            }"
            @click="updateIconSelected(icon.name)"
          >
            <component
              :is="icon.icon"
              class="h-5 w-5"
              :style="{ strokeWidth: '1.5', stroke: '#344054', fill: '#FFFFFF' }"
            />
          </div>
        </div>
      </div>
      <div class="form-group">
        <div class="flex space-x-3">
          <UITextXsMedium>{{ t("common.rating.iconAlignment") }}</UITextXsMedium>
        </div>
        <div class="flex items-start justify-start gap-2 self-stretch">
          <div
            v-for="labelType in RATING_ALIGNMENT"
            :key="labelType.key"
            class="label-box"
            :class="[labelType.key === ratingElement.iconAlignment ? 'active ' : '']"
            @click="updateIconAlignment(labelType.key)"
          >
            <UITooltip trigger="hover" placement="top">
              <template #trigger>
                <component
                  :is="labelType.icon"
                  class="focus_outline_none h-5 w-5"
                  :class="labelType.key === ratingElement.iconAlignment ? 'active-svg ' : ''"
                ></component>
              </template>
              {{ labelType.name }}
            </UITooltip>
          </div>
        </div>
      </div>
      <div class="form-group">
        <div class="flex space-x-3">
          <UITextXsMedium>{{ t("common.rating.count") }}</UITextXsMedium>
        </div>
        <UISelect
          id="count"
          :value="ratingCount"
          :options="RATING_COUNT"
          @update:value="updateRatingCount"
        ></UISelect>
      </div>
      <div class="form-group">
        <div class="flex space-x-3">
          <UITextXsMedium>{{ t("common.rating.lowestRating") }}</UITextXsMedium>
        </div>
        <UIInput
          id="count"
          class="standard-input-ghl"
          :model-value="ratingElement.lowestRating"
          @update:model-value="updateLowestRating"
          @blur="store.addToHistory()"
        ></UIInput>
      </div>
      <div class="form-group">
        <div class="flex space-x-3">
          <UITextXsMedium>{{ t("common.rating.highestRating") }}</UITextXsMedium>
        </div>
        <UIInput
          id="highestRating"
          class="standard-input-ghl"
          :model-value="ratingElement.highestRating"
          @update:model-value="updateHighestRating"
          @blur="store.addToHistory()"
        ></UIInput>
      </div>
      <div class="form-group">
        <div class="flex space-x-3">
          <UITextXsMedium
            >{{ t("common.rating.calculationType.calculationType") }}
            <UITooltip trigger="hover" placement="top" class="hl-text-input-label w-[300px]">
              <template #trigger>
                <InfoCircleIcon class="h-4 w-4 text-gray-400"></InfoCircleIcon>
              </template>
              {{ t("common.rating.calculationType.calculationTypeTooltip") }}
            </UITooltip>
          </UITextXsMedium>
        </div>
        <UISelect
          id="ratingCalculationType"
          :value="ratingElement.calculationType || RATING_CALCULATION_TYPE[0].value"
          :options="RATING_CALCULATION_TYPE"
          @update:value="updateCalculationType"
        ></UISelect>
      </div>
      <ColorInput
        :label="t('common.rating.iconSelectedState')"
        :color="ratingElement.color"
        @update:color="updateIconSelectedState"
      ></ColorInput>
      <div class="form-group mt-3">
        <ColorInput
          :label="t('common.rating.iconUnselectedState')"
          :color="ratingElement.inactiveColor || 'E5E7EB'"
          @update:color="updateIconUnselectedState"
        ></ColorInput>
      </div>
    </UICollapseItem>
  </UICollapse>
</template>

<script setup lang="ts">
import ColorInput from "@/components/common/Elements/ColorInput.vue";
import { useAppStore } from "@/store/app";
import {
  RATING_ALIGNMENT,
  RATING_CALCULATION_TYPE,
  RATING_COUNT,
  RATING_ELEMENTS,
} from "@/util/constants";
import { AlignmentPosition } from "@/util/methods";
import { InfoCircleIcon } from "@gohighlevel/ghl-icons/24/outline";
import {
  UICheckbox,
  UICollapse,
  UICollapseItem,
  UIInput,
  UIInputGroup,
  UIInputGroupLabel,
  UIInputNumber,
  UISelect,
  UITextXsMedium,
  UITooltip,
} from "@gohighlevel/ghl-ui";
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { PRODUCT_TYPE } from "../../../util/constants";
import LabelAlignment from "../Elements/LabelAlignment.vue";

const { t } = useI18n();
const store = useAppStore();

// 2. Type Definitions
interface RatingEditorProps {
  product?: string;
  ratingElement: {
    label: string;
    shortLabel?: string;
    hiddenFieldQueryKey?: string;
    fieldWidthPercentage?: number;
    required?: boolean;
    hidden?: boolean;
    hiddenFieldValue?: string;
    iconType?: string;
    iconAlignment?: string;
    count?: number;
    lowestRating?: string;
    highestRating?: string;
    calculationType?: string;
    color?: string;
    inactiveColor?: string;
    labelAlignment?: AlignmentPosition;
  };
  isRequiredFieldDisable?: boolean;
  isHiddenFieldDisable?: boolean;
  getFieldWidthPercentage?: () => number;
}

// 3. Props Declaration with Default Values
const props = withDefaults(defineProps<RatingEditorProps>(), {
  product: PRODUCT_TYPE.FORM,
  isRequiredFieldDisable: false,
  isHiddenFieldDisable: false,
  getFieldWidthPercentage: () => 100,
});

// 4. Emits Declaration
const emit = defineEmits<{
  updateLabel: [value: string];
  updateShortLabel: [value: string];
  updateFieldWidthPercentage: [value: number];
  updateRequired: [value: boolean];
  updateLowestRating: [value: string];
  updateHighestRating: [value: string];
  updateIconSelectedState: [value: string];
  updateRatingCount: [value: number];
  updateIconSelected: [value: string];
  updateIconAlignment: [value: string];
  updateCalculationType: [value: string];
  updateIconUnselectedState: [value: string];
  updateLabelAlignment: [value: AlignmentPosition];
}>();
const ratingCount = ref(props.ratingElement.count || 5);

const updateLabel = (value: string) => {
  emit("updateLabel", value);
};

const handleLabelAlignment = (value: AlignmentPosition) => {
  emit("updateLabelAlignment", value);
};

const updateShortLabel = (value: string) => {
  emit("updateShortLabel", value);
};

const updateFieldWidthPercentage = (value: number) => {
  emit("updateFieldWidthPercentage", value);
};

const updateRequired = (value: boolean) => {
  emit("updateRequired", value);
};

const updateIconSelectedState = (value: string) => {
  emit("updateIconSelectedState", value);
};

const updateIconSelected = (value: string) => {
  emit("updateIconSelected", value);
};

const updateRatingCount = (value: string) => {
  const count = parseInt(value);
  if (!isNaN(count) && count >= 1 && count <= 10) {
    ratingCount.value = count;
    emit("updateRatingCount", count);
  }
};

const updateLowestRating = (value: string) => {
  emit("updateLowestRating", value);
};

const updateHighestRating = (value: string) => {
  emit("updateHighestRating", value);
};

const updateIconAlignment = (value: string) => {
  emit("updateIconAlignment", value);
};

const updateCalculationType = (value: string) => {
  emit("updateCalculationType", value);
};

const updateIconUnselectedState = (value: string) => {
  emit("updateIconUnselectedState", value);
};
</script>

<style>
#rating-settings .n-collapse .n-collapse-item .n-collapse-item {
  margin-left: 0px !important;
}

.hl-text-input-label {
  white-space: pre-line;
}

.rating-icon-selected {
  border: 2px solid #2970ff !important;
  background-color: #eff4ff;
  border-radius: 0.5rem;
}
.rating-icon-unselected {
  border: 2px solid #eff4ff !important;
  border-radius: 0.5rem;
}
.label-box {
  display: flex;
  padding: 12px 14px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  border-radius: 8px;
  width: 100%;
  border: 1px solid #d0d5dd;
  background: #fff;
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
}

.active-svg {
  fill: #155eef;
  stroke: #155eef;
}

.focus_outline_none:focus {
  outline: none;
}
</style>
